import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Copy } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { NarratorCard } from '../components/NarratorCard';
import { usePodcastLibrary } from '../components/podcast/hooks';
import { useCopyToClipboard } from '../lib/copy';
import { PODCAST_APP_STEPS, WEEKLY_BRIEF_PROMPT, claimPlayback } from '../lib/podcast';

/**
 * Podcast setup: how to start the weekly brief, its private feed link once it
 * exists, and the narrator that voices it.
 */
export function PodcastSetupPage() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Link to="/podcast" className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
        <ArrowLeft />
        Podcast
      </Link>
      <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">
        Podcast setup
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Your weekly brief, its private feed, and the voice that reads it.
      </p>
      <WeeklyBriefCard />
      <NarratorCard onPreviewStart={claimPlayback} />
    </main>
  );
}

function WeeklyBriefCard() {
  const { state } = usePodcastLibrary();
  const { copiedKey, copyError, copy } = useCopyToClipboard();
  const feedUrl = state.status === 'ready' ? state.library.personal_feed_url : null;

  return (
    <Card className="mt-6 gap-4 px-4 sm:px-5" aria-labelledby="podcast-brief">
      <div>
        <h2 id="podcast-brief" className="font-display text-lg font-semibold text-foreground">
          Weekly brief
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Every Monday around 7am your assistant reads your week (your digests, then Slack, email,
          calendar and Linear), writes a short script, checks each claim against its source, and
          publishes a private three-minute episode.
        </p>
      </div>

      {state.status === 'loading' ? (
        <div className="h-16 animate-pulse rounded-lg bg-muted" aria-hidden />
      ) : null}
      {state.status === 'error' ? <p className="text-sm text-danger">{state.message}</p> : null}

      {state.status === 'ready' && feedUrl ? (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Your private feed</h3>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted/50 px-2.5 py-1.5 font-mono text-xs text-foreground">
              {feedUrl}
            </code>
            <Button variant="outline" size="sm" onClick={() => copy('feed', feedUrl)}>
              {copiedKey === 'feed' ? <Check /> : <Copy />}
              {copiedKey === 'feed' ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Anyone with this link can listen, so keep it to yourself.
          </p>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {PODCAST_APP_STEPS.map(({ app, steps }) => (
              <li key={app}>
                <span className="font-medium text-foreground">{app}:</span> {steps}
              </li>
            ))}
            <li>Spotify and YouTube Music can&apos;t follow private feeds.</li>
          </ul>
        </div>
      ) : null}

      {state.status === 'ready' && !feedUrl ? (
        <ol className="list-decimal space-y-2 pl-5 text-sm text-foreground">
          <li>
            Open your Almanac assistant in Claude Cowork. It needs to be producing daily digests
            already.
          </li>
          <li>
            <span>Ask it to </span>
            <Button
              variant="outline"
              size="xs"
              className="align-baseline"
              onClick={() => copy('prompt', WEEKLY_BRIEF_PROMPT)}
            >
              {copiedKey === 'prompt' ? <Check /> : <Copy />}
              {copiedKey === 'prompt' ? 'Copied' : `“${WEEKLY_BRIEF_PROMPT}”`}
            </Button>
            . It asks which narrator you want and anything the brief should always or never cover.
          </li>
          <li>
            Press <span className="font-medium">Run now</span> on the new weekly task. That
            publishes your first brief and creates your private feed, which then shows up here.
          </li>
        </ol>
      ) : null}
      {copyError ? <p className="text-xs text-danger">{copyError}</p> : null}
    </Card>
  );
}
